import { Paper, Skeleton, Stack, Typography } from '@mui/material';
import useTranslation from 'hooks/useTranslation/useTranslation';
import React from 'react';
import ChartStateMessage from 'v2/pages/Dashboard/charts/ChartStateMessage';

const SKELETON_HEIGHT = 260;

/** Plot height for the card that also shows a summary footer under its chart. */
export const CHART_HEIGHT = 220;

// What that card spends below its plot, measured: the gap to the divider, the divider itself, the
// gap after it, and the two summary rows. The cards sit in one grid row and stretch to the tallest,
// so this is the space a card without a footer would otherwise leave empty under its chart.
const CARD_BODY_GAP = 16;
const DIVIDER_HEIGHT = 1;
const SUMMARY_ROWS_HEIGHT = 48;

/**
 * Height of the chart area for a card whose body is nothing but the chart. It reclaims the space the
 * summary footer takes on the card above, so every card in the row ends up the same height with the
 * plot filling it instead of trailing off into blank space.
 *
 * A chart that renders its own legend has to fit that legend inside this budget — the legend sits
 * below the plot but within the chart's own box, so the plot gets whatever is left.
 */
export const FULL_CHART_HEIGHT = CHART_HEIGHT + CARD_BODY_GAP + DIVIDER_HEIGHT + CARD_BODY_GAP + SUMMARY_ROWS_HEIGHT;

interface Props {
  title: string;
  subtitle: string;
  isLoading: boolean;
  /**
   * When true, the error state is shown in place of the chart. The error comes first: a failed fetch
   * leaves no data either way, and the empty state would claim there was nothing to show instead of
   * admitting the fetch failed.
   */
  hasError?: boolean;
  /** When true (and there is no error), the empty state is shown in place of the chart. */
  isEmpty?: boolean;
  /** Per-chart copy for the empty state; ignored unless `isEmpty` is true. */
  emptyStateContent?: string;
  children: React.ReactNode;
}

/** Shared shell for the dashboard chart cards: heading, subheading, loading skeleton and empty/error states. */
const ChartCard: React.FC<Props> = ({ title, subtitle, isLoading, hasError, isEmpty, emptyStateContent, children }) => {
  const { translation } = useTranslation();

  const body = hasError ? (
    <ChartStateMessage
      title={translation.dashboard_chart_error_title}
      content={translation.dashboard_chart_error_content}
    />
  ) : isEmpty ? (
    <ChartStateMessage title={translation.not_enough_data} content={emptyStateContent ?? ''} />
  ) : (
    children
  );

  return (
    <Paper elevation={3} sx={{ height: '100%' }}>
      <Stack padding={2} gap={2} width="100%">
        <Stack>
          <Typography variant="subtitle2">{title}</Typography>
          <Typography variant="body2" color="text.secondary">
            {subtitle}
          </Typography>
        </Stack>

        {isLoading ? <Skeleton variant="rectangular" height={SKELETON_HEIGHT} /> : body}
      </Stack>
    </Paper>
  );
};

export default ChartCard;
